import type {CourseDetail,CourseLessonSummary,FileRecord} from '@reados/contracts';
import type {CourseCatalogView} from './library';
import type {CourseVideoBinding} from './reader-media';
import {openTab,type ReaderTab,type TabState} from './workspace-context';

const moduleOrder=['mechanics','electricity','magnetism','induction','experiments','waves'];
const moduleNames:Record<string,string>={mechanics:'力学',electricity:'电学',magnetism:'磁学',induction:'电磁感应',experiments:'电学实验',waves:'振动与波'};
export function orderedCourseModules(course:CourseDetail){
  return [...course.modules].sort((a,b)=>{
    const rank=(id:string)=>moduleOrder.includes(id)?moduleOrder.indexOf(id):moduleOrder.length;
    return rank(a.id)-rank(b.id);
  });
}
export function courseLessonFiles(lesson:CourseLessonSummary,files:FileRecord[]){
  const video=files.find(file=>file.id===lesson.videoFileId);
  const subtitle=video?files.find(file=>file.storage==='course'&&file.sandboxPath===video.sandboxPath.replace(/\.mp4$/i,'.vtt')):undefined;
  const document=files.find(file=>file.id===lesson.documentFileId);
  return {video,subtitle,document};
}
/** Navigation groups describe the curriculum, never a fictitious filesystem folder. */
export function courseCatalog(course:CourseDetail,files:FileRecord[]):CourseCatalogView{
  const catalog:CourseCatalogView={kind:'course',revision:1,folders:[],locations:[],files:[]};
  const listed=new Set<string>();let number=0;
  const addFile=(file:FileRecord|undefined,parentId:string,position:number)=>{
    if(!file||listed.has(file.id))return;listed.add(file.id);catalog.files.push(file);catalog.locations.push({kind:'file',id:file.id,parentId,position});
  };
  orderedCourseModules(course).forEach((module,index)=>{
    const parentId=`course-group:${course.id}:${module.id}`;
    catalog.folders.push({kind:'course-group',id:parentId,name:moduleNames[module.id]??module.title});
    catalog.locations.push({kind:'folder',id:parentId,parentId:null,position:index});
    module.lessons.forEach((lesson,position)=>{
      const id=`course-lesson:${course.id}:${lesson.id}`;
      catalog.folders.push({kind:'course-group',id,lessonId:lesson.id,name:`${String(++number).padStart(3,'0')} ${lesson.title}`});
      catalog.locations.push({kind:'folder',id,parentId,position});
      const sources=courseLessonFiles(lesson,files);
      addFile(sources.video,id,0);addFile(sources.subtitle,id,1);addFile(sources.document,id,2);
    });
  });
  return catalog;
}
export function courseReaderPair(lesson:CourseLessonSummary,files:FileRecord[]):FileRecord[]{
  const {video,subtitle,document}=courseLessonFiles(lesson,files);
  return [video,document??subtitle].filter((file):file is FileRecord=>!!file);
}
export function courseVideoBindings(course:CourseDetail,files:FileRecord[]):Map<string,CourseVideoBinding>{
  const result=new Map<string,CourseVideoBinding>();
  for(const module of course.modules)for(const lesson of module.lessons){
    const {video,subtitle}=courseLessonFiles(lesson,files);
    if(video)result.set(video.id,{courseId:course.id,lessonId:lesson.id,videoFileId:video.id,duration:lesson.duration,initialPosition:course.progress[lesson.id]?.position??0,subtitle});
  }
  return result;
}
export function mergeSourceFiles(...groups:FileRecord[][]):FileRecord[]{
  return [...new Map(groups.flat().map(file=>[file.id,file])).values()];
}
export function openReaderFiles(tabs:TabState,files:FileRecord[]):TabState{return files.reduce((state,file)=>openTab(state,file.id),tabs);}
export function activeCourseVideo(readers:ReaderTab[],visible:string[],focused:string|null,previous:string|null):string|null{
  const videos=visible.filter(id=>readers.some(tab=>tab.id===id&&tab.kind==='video'));
  return focused&&videos.includes(focused)?focused:previous&&videos.includes(previous)?previous:videos[0]??null;
}
export function mergeReaderFiles(readers:ReaderTab[],files:FileRecord[]):ReaderTab[]{
  const next=new Map(readers.map(tab=>[tab.id,tab]));
  for(const file of files)next.set(file.id,{id:file.id,fileId:file.id,title:file.name,kind:file.kind});
  return [...next.values()];
}
